import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, Copy, Loader2, Monitor, Pencil, Smartphone } from "lucide-react";
import toast from "react-hot-toast";

import {
  getMarketUpdatePreview, getMarketUpdateReport, updateMarketUpdateReport,
} from "../api/prospectingReportsApi";
import MarketUpdateVendorStep, { emptyVendor } from "../components/MarketUpdateVendorStep";
import MarketUpdatePropertyPicker from "../components/MarketUpdatePropertyPicker";
import MarketUpdateSendStep from "../components/MarketUpdateSendStep";
import MarketUpdateEmailFrame from "../components/MarketUpdateEmailFrame";
import EditWelcomeMessageModal from "../components/EditWelcomeMessageModal";
import { MU_RED, SKY, outlineButtonClass, redButtonClass } from "../marketUpdateTheme";

const LIST_URL = "/prospecting/market-update";

// The three numbered steps from Ignite's header, then Preview and Send.
const BUILD_STEPS = [
  { key: "vendor", title: "Vendor" },
  { key: "sales", title: "Surrounding sales" },
  { key: "listings", title: "Current listings" },
];
const STEP_KEYS = [...BUILD_STEPS.map((s) => s.key), "preview", "send"];

/**
 * The left edge and width of `ref`'s element, kept current, for a bar fixed to
 * the bottom of the window over just this page. `sticky` cannot do it: the
 * CRM's content wrapper (MainLayout) is overflow-x-hidden, which makes it the
 * sticky container, and it never scrolls, so a sticky bar sat at the very end
 * of the page (Christian, 28 Oct 2026: "cant see the back or next tab down
 * below unless I scroll all the way down").
 */
const useHorizontalBox = (ref, ready) => {
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox((prev) => (prev && prev.left === r.left && prev.width === r.width ? prev : { left: r.left, width: r.width }));
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [ref, ready]);
  return box;
};

const Stepper = ({ current, onGo }) => {
  const index = BUILD_STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="flex items-start justify-center gap-6 sm:gap-16" aria-label="Steps">
      {BUILD_STEPS.map((step, i) => {
        const done = i < index;
        const active = i === index;
        return (
          <li key={step.key}>
            <button
              type="button"
              onClick={() => onGo(step.key)}
              aria-current={active ? "step" : undefined}
              className="flex flex-col items-center gap-1.5 group focus:outline-none"
            >
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-bold transition-colors group-focus-visible:ring-2 group-focus-visible:ring-offset-2 group-focus-visible:ring-[#55c5d0]/40 ${
                  active || done ? "text-white border-transparent" : "border-gray-300 text-gray-500 bg-white"
                }`}
                style={active || done ? { backgroundColor: SKY } : undefined}
              >
                {done ? <Check className="w-4 h-4" aria-hidden="true" /> : i + 1}
              </span>
              <span className={`text-xs sm:text-sm ${active ? "font-semibold text-gray-900" : "text-gray-600"}`}>{step.title}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
};

/**
 * The property Market Update builder (Monday 13377764940):
 *
 *   Vendor > Surrounding sales > Current listings > Preview > Send
 *
 * Saved to the API whenever the agent moves between steps.
 */
const MarketUpdateWizardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = STEP_KEYS.includes(searchParams.get("step")) ? searchParams.get("step") : "vendor";

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [property, setProperty] = useState({ full_address: "", suburb: "" });
  const [vendors, setVendors] = useState([]);
  const [sales, setSales] = useState([]);
  const [listings, setListings] = useState([]);
  const [dirty, setDirty] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [showWelcome, setShowWelcome] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);
  const barBox = useHorizontalBox(rootRef, loading);

  useEffect(() => {
    getMarketUpdateReport(id)
      .then(({ data }) => {
        setReport(data);
        setProperty({ full_address: data.full_address || "", suburb: data.suburb || "" });
        setVendors(data.vendors?.length ? data.vendors : [emptyVendor()]);
        setSales(data.sales || []);
        setListings(data.listings || []);
      })
      .catch(() => {
        toast.error("That market update could not be found.");
        navigate(LIST_URL);
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const { data } = await getMarketUpdatePreview(id);
      setPreview(data);
    } catch (error) {
      toast.error("Could not build the preview.");
    } finally {
      setPreviewLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (step === "preview" && report) loadPreview();
  }, [step, report, loadPreview]);

  const markDirty = (setter) => (value) => { setter(value); setDirty(true); };

  const persist = useCallback(async () => {
    if (!dirty) return report;
    setSaving(true);
    try {
      const { data } = await updateMarketUpdateReport(id, {
        full_address: property.full_address.trim(),
        suburb: property.suburb.trim(),
        vendors: vendors.filter((v) => v.first_name || v.last_name || v.mobile || v.email),
        sales,
        listings,
      });
      setReport(data);
      setDirty(false);
      return data;
    } catch (error) {
      const detail = error?.response?.data;
      toast.error(detail?.detail || Object.values(detail || {})[0]?.[0] || "Could not save your changes.");
      throw error;
    } finally {
      setSaving(false);
    }
  }, [dirty, id, property, vendors, sales, listings, report]);

  const goTo = async (key) => {
    if (key === step) return;
    if (step === "vendor" && (!property.full_address.trim() || !property.suburb.trim())) {
      toast.error("Add the property address and suburb first.");
      return;
    }
    try { await persist(); } catch (error) { return; }
    setSearchParams({ step: key }, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveWelcome = async (text) => {
    try {
      const { data } = await updateMarketUpdateReport(id, { welcome_message: text });
      // Setting the report reloads the preview (see the effect above).
      setReport(data);
      setShowWelcome(false);
      toast.success("Welcome message saved.");
    } catch (error) {
      toast.error(error?.response?.data?.welcome_message?.[0] || "Could not save the message.");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(preview?.share_url || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Could not copy. The link is: " + (preview?.share_url || ""));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const address = property.full_address || report?.full_address;
  const building = BUILD_STEPS.some((s) => s.key === step);
  const heading = step === "preview" ? "Preview market update" : step === "send" ? "Send by SMS and email" : "Generate market update";
  const buildIndex = BUILD_STEPS.findIndex((s) => s.key === step);
  const nextKey = building ? (STEP_KEYS[buildIndex + 1]) : null;
  const backKey = building && buildIndex > 0 ? BUILD_STEPS[buildIndex - 1].key : null;
  const showBar = building || step === "preview";

  return (
    <div ref={rootRef} className={showBar ? "pb-24" : undefined}>
      <div className="px-4 md:px-6 pt-4 pb-5 bg-white border-b border-gray-200">
        <button type="button" onClick={() => navigate(LIST_URL)}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-2">
          <ChevronLeft className="w-4 h-4" /> Back to Market Update
        </button>
        <h1 className="text-center text-base sm:text-lg font-semibold text-gray-900 text-balance">
          <span className="hidden sm:inline">{heading} <span className="text-gray-400 font-normal">|</span> </span>{address}
        </h1>
        {building && <div className="mt-4"><Stepper current={step} onGo={goTo} /></div>}
      </div>

      <div className="px-4 md:px-6 pt-6">
        {step === "vendor" && (
          <MarketUpdateVendorStep
            property={property}
            onPropertyChange={(patch) => { setProperty((prev) => ({ ...prev, ...patch })); setDirty(true); }}
            vendors={vendors}
            onVendorsChange={markDirty(setVendors)}
          />
        )}
        {step === "sales" && (
          <MarketUpdatePropertyPicker report={{ ...report, ...property }} kind="sold" added={sales} onChange={markDirty(setSales)} />
        )}
        {step === "listings" && (
          <MarketUpdatePropertyPicker report={{ ...report, ...property }} kind="for_sale" added={listings} onChange={markDirty(setListings)} />
        )}

        {step === "preview" && (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-5">
            <section className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden" aria-label="Preview">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white">
                <p className="text-sm font-semibold text-gray-800">What {vendors[0]?.first_name || "the vendor"} will see</p>
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="radiogroup" aria-label="Preview size">
                  {[["desktop", Monitor, "Desktop"], ["mobile", Smartphone, "Mobile"]].map(([key, Icon, label]) => (
                    <button key={key} type="button" role="radio" aria-checked={device === key} aria-label={label}
                      onClick={() => setDevice(key)}
                      className={`px-3 py-1.5 ${device === key ? "text-white" : "bg-white text-gray-600 hover:text-gray-900"}`}
                      style={device === key ? { backgroundColor: SKY } : undefined}>
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-3 sm:p-5">
                {previewLoading && !preview ? (
                  <div className="h-[480px] rounded-lg bg-white animate-pulse" aria-hidden="true" />
                ) : preview ? (
                  <MarketUpdateEmailFrame
                    html={preview.html}
                    title="Market update preview"
                    width={device === "mobile" ? "390px" : "100%"}
                    className={`rounded-lg shadow-[0_8px_24px_-12px_rgba(17,24,39,0.25)] ${device === "mobile" ? "max-w-full" : "max-w-[680px]"}`}
                  />
                ) : null}
              </div>
            </section>

            <div className="space-y-4">
              <section className="rounded-xl border border-gray-200 bg-white">
                <h2 className="px-4 pt-4 pb-2 text-sm font-bold text-gray-900">Review your report</h2>
                <ul className="divide-y divide-gray-100 px-4 pb-1">
                  {[
                    ["Edit welcome message", () => setShowWelcome(true)],
                    ["Edit form", () => goTo("vendor")],
                  ].map(([label, onClick]) => (
                    <li key={label} className="flex items-center justify-between py-3">
                      <span className="text-sm text-gray-800">{label}</span>
                      <button type="button" onClick={onClick}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <h2 className="text-sm font-bold text-gray-900 mb-3">Send your report</h2>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={copyLink} disabled={!preview} className={`${outlineButtonClass} py-2.5`}>
                    {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
                  </button>
                  <button type="button" onClick={() => goTo("send")}
                    className="inline-flex items-center justify-center rounded-lg py-2.5 text-sm font-semibold text-white hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#55c5d0]"
                    style={{ backgroundColor: SKY }}>
                    Send SMS/Email
                  </button>
                </div>
                {sales.length === 0 && (
                  <p className="text-xs text-[#7a4b00] bg-[#fff7e6] rounded-lg p-2.5 mt-3">
                    No surrounding sales added yet. The report will only show your welcome message and profile.
                  </p>
                )}
              </section>
            </div>
          </div>
        )}

        {step === "send" && report && (
          <MarketUpdateSendStep
            report={report}
            onSent={(updated) => setReport(updated)}
            onBack={() => goTo("preview")}
            onDone={() => navigate(LIST_URL)}
          />
        )}
      </div>

      {showBar && (
        <div
          className="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-200 bg-white shadow-[0_-6px_16px_-12px_rgba(17,24,39,0.25)]"
          style={barBox ? { left: barBox.left, width: barBox.width, right: "auto" } : undefined}
          data-testid="market-update-step-bar"
        >
          <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3">
            <button type="button"
              onClick={() => (step === "preview" ? goTo("listings") : backKey ? goTo(backKey) : navigate(LIST_URL))}
              className={`${outlineButtonClass} px-4 py-2.5`}>
              Back
            </button>
            <p className="hidden sm:block text-xs text-gray-600 tabular-nums">
              {sales.length} sale{sales.length === 1 ? "" : "s"} · {listings.length} listing{listings.length === 1 ? "" : "s"} added
              {saving && " · Saving…"}
            </p>
            {step === "preview" ? (
              <button type="button" onClick={() => navigate(LIST_URL)} className={`${redButtonClass} px-6 py-2.5`} style={{ backgroundColor: MU_RED }}>
                Done
              </button>
            ) : (
              <button type="button" onClick={() => goTo(nextKey)} disabled={saving}
                className={`${redButtonClass} px-6 py-2.5`} style={{ backgroundColor: MU_RED }}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {nextKey === "preview" ? "Preview report" : "Next"}
              </button>
            )}
          </div>
        </div>
      )}

      {showWelcome && (
        <EditWelcomeMessageModal value={report?.welcome_message} onClose={() => setShowWelcome(false)} onSave={saveWelcome} />
      )}
    </div>
  );
};

export default MarketUpdateWizardPage;
